import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import View from '@components/view/view';
import Header from '@components/header/header.component';
import { Divider } from '@components/divider/divider';
import FlatButton from '@components/flat-button/flat-button';
import { hasPermissions } from '@utils/permissions';
import { usersSDK } from '@services/users';
import { useServicesStore } from '@stores/services-store';
import { usePaymentsStore } from '@stores/payment-store';
import { useAuthStore } from '@stores/auth-store';
import { PurchaseSubscription } from '@app-types/subscription.type';
import { Coupon } from '@app-types/coupon.type';
import { paymentsSDK } from '@services/payments';
import { subscriptionsSDK } from '@services/subscriptions';

import { useThemeStore } from '../../store';

import styles from './submit-purchase.module.scss';
import { PurchaseForm } from './containers/purchase-form/purchase-form';
import { PurchasePreview } from './containers/purchase-preview/purchase-preview';

type UserBasicInfo = {
    id: number;
    name: string;
    email: string;
};

const SubmitPurchaseView = () => {
    const navigate = useNavigate();
    const { theme } = useThemeStore();
    const { permissions } = useAuthStore();
    const { services, planFrequencies, fetchFrequencies, fetchServices } =
        useServicesStore();
    const { gateways, fetchGateways, exchangeRates } = usePaymentsStore();

    const [users, setUsers] = useState<UserBasicInfo[]>([]);
    const [previewShown, setPreviewShown] = useState(false);
    const [searchedCoupons, setSearchedCoupons] = useState<Coupon[]>([]);

    const pagination = useMemo(() => ({ page: 1, count: 10 }), []);

    useEffect(() => {
        fetchServices();
        fetchFrequencies();
        fetchGateways({ isActive: true });
        fetchFirstCoupons();
    }, []);

    const onSearchUsers = async (query: string) => {
        if (!hasPermissions('read-users', permissions)) return;
        if (!query) return setUsers([]);

        const response = await usersSDK.admin.getMany({
            ...pagination,
            search: query,
        });
        if (!response) return setUsers([]);
        setUsers(
            response.result.map((user) => ({
                id: user.id,
                name: `${user.firstName} ${user.lastName}`,
                email: user.email ?? '',
            }))
        );
    };

    const togglePreview = useCallback(
        () => setPreviewShown(!previewShown),
        [previewShown]
    );

    const fetchFirstCoupons = async () => {
        const response = await paymentsSDK.admin.coupons.get({
            page: 1,
            count: 3,
        });
        if (!response) return setSearchedCoupons([]);
        setSearchedCoupons(response.result);
    };

    const searchCoupons = async (query: string) => {
        if (query.trim().length === 0) return;
        const coupons = await paymentsSDK.admin.coupons.search(query.trim());
        setSearchedCoupons(coupons);
    };

    const onPurchase = async (data: PurchaseSubscription) => {
        const { created, error } =
            await subscriptionsSDK.admin.subscriptions.create(data);
        if (!created) return error;
        navigate(`/users/${data.userId}`);
    };

    return (
        <View permissions={permissions} requiredPermission="create-invoices">
            <Header
                mode={theme}
                title="Carga de pagos"
                subtitle="Este módulo te permite cargar compras de suscripciones por parte del usuario"
            />
            <div className={styles.purchaseContainer}>
                <div className={styles.header}>
                    <h3>Datos de la compra</h3>
                    <FlatButton onClick={togglePreview}>
                        {previewShown ? 'Cerrar' : 'Mostrar'} vista previa
                    </FlatButton>
                </div>
                <div className={styles.content}>
                    <PurchaseForm
                        users={users}
                        services={services}
                        gateways={gateways}
                        rates={exchangeRates}
                        frequencies={planFrequencies}
                        searchedCoupons={searchedCoupons}
                        onSearchUsers={onSearchUsers}
                        onSearchCoupon={searchCoupons}
                        onSubmit={onPurchase}
                    />
                    {previewShown && <Divider direction="vertical" />}
                    <PurchasePreview shown={previewShown} />
                </div>
            </div>
        </View>
    );
};

export default SubmitPurchaseView;
